import axiosInstance from '@/setup/axios/axiosInstance';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import {
  InventoryItemFormData,
  InventoryItemListResponse,
  InventoryItemPayload,
  InventoryItemQueryParams,
  InventoryItemRow,
  upsertItemDetailOptions,
} from '../models/ItemModel';

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

const unwrapListResponse = (data: any): InventoryItemListResponse => {
  if (Array.isArray(data)) {
    return {
      results: data,
      count: data.length,
      page: 1,
      page_size: data.length || PAGE_SIZE,
      total_pages: 1,
    };
  }

  return {
    results: data?.results || [],
    count: data?.count || 0,
    page: data?.page || 1,
    page_size: data?.page_size || PAGE_SIZE,
    total_pages: data?.total_pages || 1,
  };
};

const toRow = (item: any): InventoryItemRow => ({
  id: String(item.id),
  code: item.code || '',
  name: item.name || '',
  item_type: typeof item.item_type === 'object' && item.item_type ? String(item.item_type.id) : (item.item_type || ''),
  item_type_label: typeof item.item_type === 'object' && item.item_type ? (item.item_type.name || '') : (item.item_type_label || item.item_type || ''),
  supplier_id: item.supplier?.id || item.supplier_id || '',
  supplier_name: item.supplier?.name || item.supplier_name || '',
  manufacturer_id: item.manufacturer?.id || item.manufacturer_id || '',
  manufacturer_name: item.manufacturer?.name || item.manufacturer_name || '',
  group_id: item.group?.id || item.group_id || '',
  group_name: item.group?.name || item.group_name || '',
  uom_id: item.uom?.id || item.uom_id || '',
  uom_name: item.uom?.name || item.uom_name || '',
  status: item.status?.id || item.status || 'approved',
  status_label: item.status?.name || item.status_label || '',
  company_id: item.company?.id || item.company_id || '',
  company_name: item.company?.name || item.company_name || '',
  specification: item.specification || '',
  image1: item.image1 || '',
  image2: item.image2 || '',
  track_lot: Boolean(item.track_lot),
  track_location: Boolean(item.track_location),
  last_price: item.last_price || undefined,
});

const toForm = (item: any): InventoryItemFormData & { id: string } => ({
  id: String(item.id),
  code: item.code || '',
  name: item.name || '',
  group_id: item.group?.id || item.group_id || '',
  supplier_id: item.supplier?.id || item.supplier_id || '',
  item_type: typeof item.item_type === 'object' && item.item_type ? String(item.item_type.id) : (item.item_type || ''),
  uom_id: item.uom?.id || item.uom_id || '',
  manufacturer_id: item.manufacturer?.id || item.manufacturer_id || '',
  status: item.status?.id || item.status || 'approved',
  company_id: item.company?.id || item.company_id || '',
  specification: item.specification || '',
  image1: item.image1 || '',
  image2: item.image2 || '',
  track_lot: Boolean(item.track_lot),
  track_location: Boolean(item.track_location),
});

const toPayload = (payload: Partial<InventoryItemPayload>) => {
  const result: Record<string, any> = {};
  const trimmedCode = payload.code?.trim();
  if (trimmedCode !== undefined) result.code = trimmedCode;
  const trimmedName = payload.name?.trim();
  if (trimmedName !== undefined) result.name = trimmedName;
  if (payload.company_id !== undefined) result.company = payload.company_id;
  if (payload.group_id !== undefined) result.group_id = payload.group_id || null;
  if (payload.supplier_id !== undefined) result.supplier_id = payload.supplier_id || null;
  if (payload.item_type !== undefined) result.item_type = payload.item_type || '';
  if (payload.uom_id !== undefined) result.uom_id = payload.uom_id || null;
  if (payload.manufacturer_id !== undefined) result.manufacturer_id = payload.manufacturer_id || null;
  const trimmedSpec = payload.specification?.trim();
  if (trimmedSpec !== undefined) result.specification = trimmedSpec;
  if (payload.image1 !== undefined) result.image1 = payload.image1 || '';
  if (payload.image2 !== undefined) result.image2 = payload.image2 || '';
  if (payload.track_lot !== undefined) result.track_lot = payload.track_lot;
  if (payload.track_location !== undefined) result.track_location = payload.track_location;
  return result;
};

export const getItems = async ({ search = '', status = 'all', companyId = '', page = 1, pageSize = PAGE_SIZE }: InventoryItemQueryParams = {}): Promise<InventoryItemListResponse> => {
  const params: Record<string, string | number> = {
    page,
    page_size: pageSize || PAGE_SIZE,
  };

  if (search.trim()) {
    params.search = search.trim();
  }
  if (status && status !== 'all') {
    params.status = status;
  }
  if (companyId) {
    params.company = companyId;
  }

  const response = await axiosInstance.get('/inventory/items/', { params });
  const parsed = unwrapListResponse(response.data);

  return {
    ...parsed,
    results: (parsed.results || []).map(toRow),
  };
};

export const getItem = async (itemId: string): Promise<InventoryItemFormData & { id: string }> => {
  const response = await axiosInstance.get(`/inventory/items/${itemId}/`);
  upsertItemDetailOptions(response.data);
  return toForm(response.data);
};

export const createItem = async (payload: InventoryItemPayload): Promise<InventoryItemRow> => {
  const response = await axiosInstance.post('/inventory/items/', toPayload(payload));
  return toRow(response.data);
};

export const updateItem = async (itemId: string, payload: Partial<InventoryItemPayload>): Promise<InventoryItemRow> => {
  const response = await axiosInstance.patch(`/inventory/items/${itemId}/`, toPayload(payload));
  return toRow(response.data);
};

export const updateItemStatus = async (itemId: string, status: 'draft' | 'approved' | 'inactive'): Promise<InventoryItemRow> => {
  const response = await axiosInstance.patch(`/inventory/items/${itemId}/`, { status });
  return toRow(response.data);
};

export const deleteItem = async (itemId: string): Promise<void> => {
  await axiosInstance.delete(`/inventory/items/${itemId}/`);
};